import { Component, OnInit, ViewChild, AfterViewInit, ElementRef } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router, ActivatedRoute } from "@angular/router";
import { TechnicalLetterService } from "../../services/technical-letters/technical-letter.service";
import { LetterEquipment } from "../../interfaces/technical-letters/letter-equipment.models";
import { LetterDispositionLkp } from "../../interfaces/technical-letters/letter-disposition-lkp.models";
import { LookupService } from "../../services/technical-letters/lookup.service";
import { finalize, forkJoin } from "rxjs";
import { EditTechnicalLetter } from "../../interfaces/technical-letters/edit-technical-letter.models";
import { LoggedUserService } from "../../services/common/logged-user.service";
import * as ExcelJS from "exceljs";

@Component({
  selector: "app-edit-technical-letters",
  standalone: false,
  templateUrl: "./edit-technical-letters.component.html",
  styleUrls: ["./edit-technical-letters.component.css"],
})
export class EditTechnicalLettersComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  loading = false;
  isGridLoading = false;
  error: string | null = null;
  validationMessages: string[] = [];
  issuers = [];
  numbers = [];
  categories = [];
  statuses = [];
  letterId!: number;
  userEmail: string = "";
  editLetterFormData = new FormData();

  letter: any = {
    issuer: "",
    number: "",
    title: "",
    category: "",
    status: "",
    issuerdate: "",
    fileUrl: "",
    fileName: "",
    integrityCriticalTask: false,
    showAllRecords: true,
    recurring: false,
  };

  columns = [
    { key: "assigned", label: "Assigned", filterable: true },
    { key: "equipmentSubType", label: "Equipment Type", filterable: true },
    { key: "businessUnit", label: "Business Unit", filterable: true },
    { key: "plant", label: "Plant", filterable: true },
    { key: "equipment", label: "Equipment #", filterable: true },
    { key: "model", label: "Model", filterable: true },
    { key: "shortName", label: "Short Name", filterable: true },
    { key: "disposition", label: "Disposition", filterable: true },
    { key: "comments", label: "Comments", filterable: true },
    { key: "outageName", label: "Outage Name", filterable: true },
    { key: "actions", label: "Action", filterable: false },
    { key: "doc", label: "Doc", filterable: false },
    { key: "log", label: "History", filterable: false },
  ];

  columnKeys = this.columns.map((col) => col.key);
  dataSource = new MatTableDataSource<LetterEquipment>();
  letterEquipmentData: LetterEquipment[] = [];
  filteredData: LetterEquipment[] = [];
  showAllRecords: boolean = true;
  letterData: LetterEquipment[] = [];
  dispositions: LetterDispositionLkp[] = [];
  editingRowIndex: number | null = null;
  editCache: Partial<LetterEquipment> = {};
  filters: { [key: string]: string } = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private technicalLetterService: TechnicalLetterService,
    private lookupService: LookupService,
    private loggedUserService: LoggedUserService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.letterId = +id;
    }   
    const equipmentId = this.route.snapshot.queryParamMap.get("equipmentId");  
    this.loggedUserService.getLoggedUserData().subscribe({
      next: (data) => {
        this.userEmail = data?.email || "";
      }
    });
    this.isGridLoading = true;
    this.technicalLetterService
      .getLetterEquipmentByLetterId(this.letterId)
      .subscribe({
        next: (data: LetterEquipment[]) => {
          this.error = null;
          let processedData = data.map((item) => ({
            ...item,
            disposition: item.letterDispositionLkp?.lookupValue || "",
            equipment: item.equipmentNumber || "",
          }));
          
          // If equipmentId is provided, sort to show that equipment first
          if (equipmentId) {
            const targetEquipmentId = +equipmentId;
            const targetExists = processedData.some(item => item.equipmentId === targetEquipmentId);
            if (targetExists) {
              processedData = processedData.sort((a, b) => {
                if (a.equipmentId === targetEquipmentId) return -1;
                if (b.equipmentId === targetEquipmentId) return 1;
                return 0;
              });
            }
          }
          
          this.letterEquipmentData = processedData;
          this.filteredData = [...this.letterEquipmentData];
          this.applyFilters();
          this.isGridLoading = false;
        },
        error: () => {
          this.isGridLoading = false;
        },
      });
    this.loadLetterDetails();
    this.loadDispositions();
    this.loadDropdownData();
  }

  private loadDropdownData(issuerId: number = 0, callback?: () => void): void {
    this.loading = true;
    forkJoin({
      issuers: this.lookupService.getLookups(3),
      statuses: this.technicalLetterService.getAllStatuses(issuerId),
      categories: this.technicalLetterService.getAllCategories(issuerId),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          if (callback) callback();
        })
      )
      .subscribe({
        next: (data) => {
          this.issuers = data.issuers.map((issuer: any) => ({
            value: issuer.pid,
            label: issuer.lookupValue,
          }));
          this.statuses = data.statuses.map((status: any) => ({
            value: status,
            label: status,
          }));
          this.categories = data.categories.map((category: any) => ({
            value: category,
            label: category,
          }));
        },
        error: (error) => {
          console.error("Error fetching dropdown data:", error);
          this.error = "Failed to load dropdown data.";
        },
      });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadLetterDetails(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.loading = true;
      this.technicalLetterService.getTechnicalLetterById(Number(id)).subscribe({
        next: (response) => {
          const issuerId = response.letterIssuerLkpId;

          this.loadDropdownData(issuerId, () => {
            this.letter = {
              id: response.id,
              issuer: issuerId,
              number: response.letterNumber,
              title: response.letterTitle,
              category: response.letterCategory,
              status: response.letterStatus,
              issuerdate: response.letterIssueDate?.split("T")[0],
              fileUrl: response.letterUrl,
              fileName: response.letterTitle,
              integrityCriticalTask: response.ict,
              recurring: response.letterRecurringFlag,
              rowCreatedDate: response.rowCreatedDate,
              rowUpdatedDate: response.rowUpdatedDate,
              showAllRecords: false,
            };
          });
        },
        error: (err) => {
          this.error = "Failed to load the technical letter.";
          console.error("Error fetching technical letter:", err);
          this.loading = false;
        },
      });
    } else {
      this.error = "No ID provided.";
    }
  }

  loadDispositions(): void {
    this.lookupService.getLookups(2).subscribe({
      next: (data: LetterDispositionLkp[]) => {
        this.dispositions = data;
      },
      error: () => {
        this.error = "Failed to load dispositions";
      },
    });
  }

  toggleRecords() {
    this.applyFilters();
  }

  applyFilters() {
    const filtered = this.showAllRecords
      ? [...this.letterEquipmentData]
      : this.letterEquipmentData.slice(0, 1);

    this.filteredData = filtered.filter((row) =>
      this.columns.every((col) => {
        if (!col.filterable) return true;
        const filterValue = (this.filters[col.key] || "").toLowerCase();
        const rowValue = (row[col.key as keyof LetterEquipment] || "")
          .toString()
          .toLowerCase();
        return !filterValue || rowValue.includes(filterValue);
      })
    );
    this.dataSource.data = this.filteredData;
  }

  onFilterInput(colKey: string, value: string) {
    this.filters[colKey] = value;
    this.applyFilters();
  }

  onEditRow(row: LetterEquipment, index: number): void {
    this.editingRowIndex = index;
    this.editCache = { ...row };
  }

  onDispositionChange(selectedValue: string) {
    const selected = this.dispositions.find(
      (d) => d.lookupValue === selectedValue
    );
    if (selected) {
      this.editCache.letterDispositionLkp = selected;
    }
  }

  onSaveRow(index: number) {
    const row = this.dataSource.data[index];

    const payload: LetterEquipment = {
      ...row,
      comments: this.editCache.comments ?? row.comments,
      outageName: this.editCache.outageName ?? row.outageName,
      rowUpdatedBy: this.userEmail,
      rowUpdatedDate: new Date().toISOString(),
      letterDispositionLkp:
        this.editCache.letterDispositionLkp ?? row.letterDispositionLkp,
    };

    this.technicalLetterService.updateLetterEquipment(payload).subscribe({
      next: () => {
        row.letterDispositionLkp = payload.letterDispositionLkp;
        (row as any).disposition = payload.letterDispositionLkp?.lookupValue || "";
        row.comments = payload.comments;
        row.outageName = payload.outageName;
        row.rowUpdatedBy = payload.rowUpdatedBy;
        row.rowUpdatedDate = payload.rowUpdatedDate;

        this.editingRowIndex = null;
        this.editCache = {};
      },
      error: () => {
        this.error = "Failed to update equipment data";
      },
    });
  }

  onSave(): void {
    this.validationMessages = this.validateLetterForm();
    if (this.validationMessages.length > 0) {
      return;
    }

    this.editLetterFormData.set('id', this.letter.id?.toString() || '');
    this.editLetterFormData.set('letterNumber', this.letter.number || '');
    this.editLetterFormData.set('letterTitle', this.letter.title || '');
    this.editLetterFormData.set('letterIssuerLkpId', this.letter.issuer?.toString() || '');
    this.editLetterFormData.set('letterStatus', this.letter.status || '');
    this.editLetterFormData.set('letterCategory', this.letter.category || '');
    this.editLetterFormData.set('letterIssueDate', this.letter.issuerdate || '');
    this.editLetterFormData.set('letterUrl', this.letter.fileUrl || '');
    this.editLetterFormData.set('letterRecurringFlag', this.letter.recurring?.toString() || 'false');
    this.editLetterFormData.set('rowCreatedBy', this.letter.rowCreatedBy || '');
    this.editLetterFormData.set('rowCreatedDate', this.letter.rowCreatedDate || '');
    this.editLetterFormData.set('rowUpdatedBy', this.userEmail);
    this.editLetterFormData.set('rowUpdatedDate', new Date().toISOString());
    this.editLetterFormData.set('ict', this.letter.integrityCriticalTask?.toString() || 'false');
    this.editLetterFormData.set('letterEquipments', JSON.stringify(this.letter.letterEquipments || []));
    this.editLetterFormData.set('letterHistories', JSON.stringify(this.letter.letterHistories || []));
    this.editLetterFormData.set('lookUps', this.letter.lookUps ? JSON.stringify(this.letter.lookUps) : '');

    this.loading = true;
    this.technicalLetterService.updateTechnicalLetter(this.editLetterFormData).subscribe({
      next: (response) => {
        this.loading = false;
        this.letter = {
          ...this.letter,
          id: response.id,
          number: response.letterNumber,
          title: response.letterTitle,
          issuer: response.letterIssuerLkpId,
          status: response.letterStatus,
          category: response.letterCategory,
          issuerdate: response.letterIssueDate?.split("T")[0],
          fileUrl: response.letterUrl,
          fileName: response.letterTitle,
          integrityCriticalTask: response.ict,
          recurring: response.letterRecurringFlag,
        }
        this.editLetterFormData = new FormData();
        if (this.fileInput) {
          this.fileInput.nativeElement.value = '';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = "Failed to update the technical letter.";
        console.error("Error updating technical letter:", err);
      },
    });
  }

  validateLetterForm(): string[] {
    if (!this.letter) {
      this.validationMessages = ["Letter object is not initialized."];
      return;
    }
    const errors: string[] = [];
    if (!this.letter.number || this.letter.number.trim() === "")
      errors.push("Number is required.");
    if (
      !this.letter.title ||
      typeof this.letter.title !== "string" ||
      this.letter.title.trim() === ""
    )
      errors.push("Title is required.");
    if (
      !this.letter.category ||
      typeof this.letter.category !== "string" ||
      this.letter.category.trim() === ""
    )
      errors.push("Category is required.");
    if (
      !this.letter.status ||
      typeof this.letter.status !== "string" ||
      this.letter.status.trim() === ""
    )
      errors.push("Status is required.");
    if (
      !this.letter.issuerdate ||
      typeof this.letter.issuerdate !== "string" ||
      this.letter.issuerdate.trim() === ""
    )
      errors.push("Issuer Date is required.");
    return errors;
  }

  onCancelEditRow() {
    this.editingRowIndex = null;
    this.editCache = {};
  }

  onDeleteRow(index: number) {
    const row = this.filteredData[index];
    if (!row || !row.id) {
      this.error = "Invalid row selected for deletion.";
      return;
    }

    if (confirm("Are you sure you want to delete this equipment?")) {
      this.technicalLetterService.deleteLetterEquipment(row.id, this.userEmail).subscribe({
        next: () => {
          const globalIndex = this.letterEquipmentData.findIndex(
            (r) => r.id === row.id
          );
          if (globalIndex > -1) {
            this.letterEquipmentData.splice(globalIndex, 1);
          }
          this.applyFilters();
        },
        error: () => {
          this.error = "Failed to delete equipment.";
        },
      });
    }
  }

  openAddEquipment(letterId: number) {
    const width = 1000;
    const height = 500;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const urlTree = this.router.createUrlTree(
      ["/technical-letters/add-equipment", letterId],
      { queryParams: { popup: true } }
    );
    const url = window.location.origin + this.router.serializeUrl(urlTree);
    window.open(
      url,
      "AddEquipmentPopup",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );
  }

  onDoc(row: any) {
    const letterEquipmentId = row.id;
    if (!letterEquipmentId) {
      console.error("Row or letterEquipmentId is missing:", row);
      return;
    }

    const width = 800;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const urlTree = this.router.createUrlTree(
      ["/technical-letters/letter-equipment-doc", letterEquipmentId],
      { queryParams: { popup: true } }
    );

    const url = window.location.origin + this.router.serializeUrl(urlTree);
    window.open(
      url,
      "DocPopup",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );
  }

  onLog(row: any, logType: string) {
    const letterId = row.letterId || row.id;
    if (!letterId) {
      console.error("Row or letterId/id is missing:", row);
      return;
    }
    const width = 800;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    let urlTree;

    if (logType === "equipment") {
      const equipmentHistoryId = row.id;
      urlTree = this.router.createUrlTree(
        ["/technical-letters/history-log", equipmentHistoryId],
        {
          queryParams: { popup: true },
        }
      );
    } else if (logType === "letter") {
      urlTree = this.router.createUrlTree(
        ["/technical-letters/letter-history", letterId],
        { queryParams: { popup: true } }
      );
    } else {
      console.error("Invalid log type:", logType);
      return;
    }

    const url = window.location.origin + this.router.serializeUrl(urlTree);
    window.open(
      url,
      "LogPopup",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );
  }
  onPageChange(event: any) {
    this.editingRowIndex = null;
    this.editCache = {};
  }

  goToDeletedEquipmentLog(): void {
    const urlTree = this.router.createUrlTree(
      ["/technical-letters/deleted-equipment-log", this.letter.id]
    );
    const url = window.location.origin + this.router.serializeUrl(urlTree);
    window.open(url, "_blank");
  }
  onCancel(): void {
    this.router.navigate(["/technical-letters"]);
  }

  onDelete(): void {
    if (!this.letter.id) {
      this.error = "No letter selected for deletion.";
      return;
    }
    if (confirm("Are you sure you want to delete this technical letter?")) {
      this.loading = true;
      this.technicalLetterService
        .deleteTechnicalLetter(this.letter.id, this.userEmail)
        .subscribe({
          next: () => {
            this.loading = false;
            this.router.navigate(["/technical-letters"]);
          },
          error: (err) => {
            this.loading = false;
            this.error = "Failed to delete the technical letter.";
            console.error("Error deleting technical letter:", err);
          },
        });
    }
  }

  onFileSelected(event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.editLetterFormData.set("file", file);
    }
  }

  exportToExcel() {
    const headerRow = [
      "CreateDate",
      "plantID",
      "tblName",
      "equipID",
      "equipType",
      "equipSubType",
      "BUName",
      "PlantName",
      "SerialNumber",
      "ShortName",
      "Model",
      "Disposition",
      "Comments",
      "Outage_Name",
      "Isdoc",
      "le_id",
      "letterId",
    ];
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Letter_Equipment");
    worksheet.addRow(headerRow);
    worksheet.getRow(1).font = {
      bold: true,
      size: 12,
      color: { argb: "000000" },
    };
    worksheet.getRow(1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    const rows = this.letterEquipmentData.map((item) => [
      new Date(item.rowCreatedDate).toLocaleDateString(),
      item.plantId,
      item.tblName,
      item.equipmentNumber,
      item.equipmentType,
      item.equipmentSubType,
      item.businessUnit,
      item.plant,
      item.equipmentNumber,
      item.shortName,
      item.model,
      item.letterDispositionLkp?.lookupValue || "",
      item.comments || "",
      item.outageName || "",
      item.isDoc,
      item.id,
      this.letterId,
    ]);
    worksheet.addRows(rows);
    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
        };
        const cellValue = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = maxLength + 5;
    });
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Letter_Equipment.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }
}
